import type { OfferMapping, PromoFieldConfig } from '../config/promoConfig';
import type { FieldRule } from '../config/fieldRulesConfig';
import type { RuleValues } from '../rules/types';
import { deriveFieldStatesForScope } from '../rules/engine/deriveFieldStates';

// -- Helper types for the nested offer structure --

interface OfferPayload {
    method: string;
    params: {
        method: string;
        params: Record<string, unknown>;
    };
}

// -- Internal helpers --

/** Normalizes offerMapping to always be an array for uniform processing. */
const toArray = (mapping: OfferMapping | OfferMapping[]): OfferMapping[] =>
    Array.isArray(mapping) ? mapping : [mapping];

/** Returns true if value is undefined, null, or empty string. */
const hasNoValue = (value: unknown): boolean => value === undefined || value === null || value === '';

/**
 * Engine-derived visibility for the promo scope, keyed by bare field name. Replaces
 * the legacy `field.visibleWhen`/`enabledWhen` gate: a param field counts as active
 * for the reconstructed offer only when its visibility rule passes against the
 * already-reconstructed method/sub-method values. Prevents inactive offer fields
 * (e.g. buyFor) from being populated when they share a param name with the active
 * offer field (e.g. buyGet both declare paramName 'buy'). Fields without a
 * visibility rule are absent from the map (treated as visible by the caller).
 */
const deriveVisibleFields = (rules: FieldRule[], formValues: Record<string, unknown>): Record<string, boolean> => {
    const qualifiedValues: RuleValues = {};
    for (const [key, value] of Object.entries(formValues)) {
        qualifiedValues[`promo:${key}`] = value;
    }
    const states = deriveFieldStatesForScope(rules, 'promo', qualifiedValues, {});
    const visibleByField: Record<string, boolean> = {};
    for (const [name, state] of Object.entries(states)) {
        visibleByField[name] = state.visible;
    }
    return visibleByField;
};

/**
 * Translates a form value using the valueMap (e.g. 'combination_offer' -> 'buy_get').
 * Returns original value if no map or no matching entry.
 */
const translateToApiValue = (formValue: string, valueMap?: Record<string, string>): string => {
    if (!valueMap) return formValue;
    return valueMap[formValue] ?? formValue;
};

/**
 * Reverse-translates an API value back to the form value using the valueMap.
 * E.g. 'buy_get' -> 'combination_offer'
 */
const translateToFormValue = (apiValue: string, valueMap?: Record<string, string>): string => {
    if (!valueMap) return apiValue;
    const reversedEntry = Object.entries(valueMap).find(([, mapped]) => mapped === apiValue);
    return reversedEntry ? reversedEntry[0] : apiValue;
};

const extractParamValue = (formValue: unknown, rangeIndex?: 'first' | 'second'): unknown => {
    if (!rangeIndex) return formValue;
    if (!Array.isArray(formValue)) return undefined;
    return rangeIndex === 'first' ? formValue[0] : formValue[1];
};

// -- Public functions --

/**
 * Builds the nested offer payload from flat form values using offerApiMapping on each field.
 * Returns null if no offer method is selected (meaning offer should not be sent).
 *
 * Output shape: { method: '...', params: { method: '...', params: { key: value, ... } } }
 */
export function buildOfferPayload(
    fields: PromoFieldConfig[],
    formValues: Record<string, unknown>,
): OfferPayload | null {
    // Step 1: Find the top-level method (e.g. 'manual' or 'promo_library')
    let topMethod: string | null = null;
    let subMethodValue: string | null = null;
    let subMethodValueMap: Record<string, string> | undefined;

    for (const field of fields) {
        if (!field.offerMapping) continue;

        const mappings = toArray(field.offerMapping);
        for (const mapping of mappings) {
            if (mapping.role === 'method') {
                const rawValue = formValues[field.name];
                if (hasNoValue(rawValue)) return null;
                topMethod = rawValue as string;
            }
            if (mapping.role === 'subMethod') {
                const rawValue = formValues[field.name];
                if (!hasNoValue(rawValue)) {
                    subMethodValue = rawValue as string;
                    subMethodValueMap = mapping.valueMap;
                }
            }
        }
    }

    // If no top method selected, return null (offer is empty)
    if (!topMethod) return null;

    // If no sub-method selected, offer is incomplete — not ready for API
    if (!subMethodValue) return null;

    // Step 2: Translate sub-method form value to API value
    const apiSubMethod = translateToApiValue(subMethodValue, subMethodValueMap);

    // Step 3: Collect all param values from fields with role: 'param'
    const offerParams: Record<string, unknown> = {};

    for (const field of fields) {
        if (!field.offerMapping) continue;

        const mappings = toArray(field.offerMapping);
        for (const mapping of mappings) {
            if (mapping.role !== 'param') continue;
            if (!mapping.paramName) continue;

            // Extract the value (handles range tuples and plain values)
            const rawFormValue = formValues[field.name];
            const paramValue = extractParamValue(rawFormValue, mapping.rangeIndex);

            // Skip empty params
            if (hasNoValue(paramValue)) continue;

            offerParams[mapping.paramName] = paramValue;

            if (mapping.unitParamName && field.unitToggle?.unitField) {
                const unitFormValue =
                    (formValues[field.unitToggle.unitField] as string | undefined) ?? field.unitToggle.defaultValue;
                if (unitFormValue) {
                    const apiUnitValue = mapping.unitValues
                        ? (mapping.unitValues[unitFormValue] ?? unitFormValue)
                        : unitFormValue;
                    offerParams[mapping.unitParamName] = apiUnitValue;
                }
            }
        }
    }

    return {
        method: topMethod,
        params: {
            method: apiSubMethod,
            params: offerParams,
        },
    };
}

/**
 * Builds flat form values from a nested offer response object.
 * Reverses the mapping: reads the nested structure and sets form field values.
 *
 * Input shape: { method: '...', params: { method: '...', params: { key: value, ... } } }
 */
export function buildOfferResponseToFormValues(
    fields: PromoFieldConfig[],
    offer: Record<string, unknown>,
    rules: FieldRule[],
): Record<string, unknown> {
    const formValues: Record<string, unknown> = {};

    const topMethod = offer.method as string | undefined;
    if (!topMethod) return formValues;

    const outerParams = offer.params as Record<string, unknown> | undefined;
    const subMethod = outerParams?.method as string | undefined;
    const innerParams = outerParams?.params as Record<string, unknown> | undefined;

    // Step 1: Set the top method field
    for (const field of fields) {
        if (!field.offerMapping) continue;

        const mappings = toArray(field.offerMapping);
        for (const mapping of mappings) {
            if (mapping.role === 'method') {
                formValues[field.name] = topMethod;
            }
        }
    }

    // Step 2: Set the sub-method field (reverse value map)
    if (subMethod) {
        for (const field of fields) {
            if (!field.offerMapping) continue;

            const mappings = toArray(field.offerMapping);
            for (const mapping of mappings) {
                if (mapping.role === 'subMethod') {
                    formValues[field.name] = translateToFormValue(subMethod, mapping.valueMap);
                }
            }
        }
    }

    // Step 3: Set param fields from the inner params object
    if (!innerParams) return formValues;

    // Engine-derived visibility against the reconstructed method/sub-method values.
    const visibleByField = deriveVisibleFields(rules, formValues);

    // Build a lookup: paramName -> { field, mapping } for efficient matching.
    // Only consider fields active for the reconstructed sub-method so that offer fields sharing
    // a param name (buyGet/buyFor/buySave all use 'buy') don't get phantom values from another
    // offer's payload — which would otherwise keep the form permanently dirty.
    const paramFieldLookup: { field: PromoFieldConfig; mapping: OfferMapping }[] = [];
    for (const field of fields) {
        if (!field.offerMapping) continue;
        if (visibleByField[field.name] === false) continue;
        const mappings = toArray(field.offerMapping);
        for (const mapping of mappings) {
            if (mapping.role === 'param' && mapping.paramName) {
                paramFieldLookup.push({ field, mapping });
            }
        }
    }

    // Group param mappings by field name to reconstruct range tuples
    const rangeValues = new Map<string, [unknown, unknown]>();

    for (const { field, mapping } of paramFieldLookup) {
        const apiValue = innerParams[mapping.paramName!];
        if (apiValue === undefined || apiValue === null) continue;

        if (mapping.rangeIndex) {
            // This is part of a range tuple — collect both parts
            const existing = rangeValues.get(field.name) ?? [undefined, undefined];
            if (mapping.rangeIndex === 'first') existing[0] = apiValue;
            if (mapping.rangeIndex === 'second') existing[1] = apiValue;
            rangeValues.set(field.name, existing);
        } else {
            // Simple single-value param
            formValues[field.name] = apiValue;
        }

        // Reverse-map the unit value back to form value (read unit field from field.unitToggle)
        if (mapping.unitParamName && field.unitToggle?.unitField) {
            const apiUnitValue = innerParams[mapping.unitParamName] as string | undefined;
            if (apiUnitValue && mapping.unitValues) {
                const reversedUnitEntry = Object.entries(mapping.unitValues).find(
                    ([, apiVal]) => apiVal === apiUnitValue,
                );
                formValues[field.unitToggle.unitField] = reversedUnitEntry ? reversedUnitEntry[0] : apiUnitValue;
            } else if (apiUnitValue) {
                formValues[field.unitToggle.unitField] = apiUnitValue;
            }
        }
    }

    // Write reconstructed range tuples into form values
    for (const [fieldName, tuple] of rangeValues) {
        formValues[fieldName] = tuple;
    }

    return formValues;
}
